import { inject, Injectable } from '@angular/core';
import { AuthService, IStorage, LocalStorage } from '@isophi/core-legacy';
import { BaseComponent } from '@isophi/core-ui';
import { BehaviorSubject, combineLatest, distinctUntilChanged, takeUntil } from 'rxjs';

import { UserService } from '../../../core/services/user.service';
import { LocalStorageConstService } from '../local-storage-const.service';

type StoredGroupAndChildrenFilter = {
  group: string | null;
  children: string[];
  workplaceUuid: string | null;
};

@Injectable({
  providedIn: 'root',
})
export class GroupAndChildrenFilterService extends BaseComponent {
  private _selectedGroupUuid = new BehaviorSubject<string | null>(null);
  private _selectedChildrenUuids = new BehaviorSubject<string[]>([]);

  selectedGroupUuid$ = this._selectedGroupUuid.asObservable().pipe(distinctUntilChanged());
  selectedChildrenUuids$ = this._selectedChildrenUuids.asObservable().pipe(distinctUntilChanged());

  private readonly lSConstService = inject(LocalStorageConstService);
  private readonly authService = inject(AuthService);
  private readonly userService = inject(UserService);
  private readonly storage: IStorage = new LocalStorage();

  get selectedGroupUuid(): string | null {
    return this._selectedGroupUuid.value;
  }

  setSelectedGroupUuid(uuid: string | null): void {
    this._selectedGroupUuid.next(uuid);
  }

  get selectedChildrenUuids(): string[] {
    return this._selectedChildrenUuids.value;
  }

  setSelectedChildrenUuids(uuids: string[]): void {
    this._selectedChildrenUuids.next(uuids);
  }

  constructor() {
    super();

    const currentWorkplaceUuid = this.userService.getCurrentKindergartenUUID();

    if (this.storage.hasItem(this.lSConstService.childrenPortfolioGroupAndChildrenFilter)) {
      const storageData = this.storage.getItem(this.lSConstService.childrenPortfolioGroupAndChildrenFilter);
      if (storageData && storageData !== 'undefined') {
        const parsedData: Partial<StoredGroupAndChildrenFilter> = JSON.parse(storageData);
        const isCurrentWorkplaceFilter = parsedData.workplaceUuid === currentWorkplaceUuid;

        if (isCurrentWorkplaceFilter) {
          this._selectedGroupUuid.next(parsedData.group ?? null);
          this._selectedChildrenUuids.next(parsedData.children ?? []);
        } else {
          this.storage.removeItem(this.lSConstService.childrenPortfolioGroupAndChildrenFilter);
        }
      }
    }

    combineLatest([this._selectedGroupUuid, this._selectedChildrenUuids])
      .pipe(distinctUntilChanged(), takeUntil(this.ngUnsubscribe$))
      .subscribe(([group, children]) => {
        const filterToStore: StoredGroupAndChildrenFilter = {
          group,
          children,
          workplaceUuid: this.userService.getCurrentKindergartenUUID(),
        };

        this.storage.setItem(this.lSConstService.childrenPortfolioGroupAndChildrenFilter, JSON.stringify(filterToStore));
      });

    // On user logout clear cache and subjects to prevent unecesary BE calls upon next login
    this.authService.onLogout$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      this.storage.removeItem(this.lSConstService.childrenPortfolioGroupAndChildrenFilter);
      this._selectedGroupUuid.next(null);
      this._selectedChildrenUuids.next([]);
    });
  }
}
